import {
  Card,
  Col,
  DatePicker,
  Empty,
  InputNumber,
  Row,
  Select,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { reportsApi, type ChronicAbsenteeRow } from '@/api/reports';
import { setupApi } from '@/api/setup';
import { PageHeader } from '@/shared/components/page-header';
import { ExportButton } from '@/shared/components/ui';

type Scope = 'CLASS_SECTION' | 'ACADEMIC_YEAR';

export function ChronicAbsenteesPage() {
  const [scope, setScope] = useState<Scope>('ACADEMIC_YEAR');
  const [yearId, setYearId] = useState<number | undefined>();
  const [classSectionId, setClassSectionId] = useState<number | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('month'), dayjs()]);
  const [thresholdPct, setThresholdPct] = useState<number>(20);

  const yearsQuery = useQuery({
    queryKey: ['setup', 'academic-years'],
    queryFn: () => setupApi.listAcademicYears(),
  });

  const sectionsQuery = useQuery({
    queryKey: ['setup', 'class-sections', yearId],
    queryFn: () => setupApi.listClassSections(yearId),
    enabled: scope === 'CLASS_SECTION' && !!yearId,
  });

  // Default-select current year on first paint.
  useEffect(() => {
    if (!yearId && yearsQuery.data) {
      const cur = yearsQuery.data.find((y) => y.current) ?? yearsQuery.data[0];
      if (cur) setYearId(cur.id);
    }
  }, [yearId, yearsQuery.data]);

  const params = {
    classSectionId: scope === 'CLASS_SECTION' ? classSectionId : undefined,
    academicYearId: scope === 'ACADEMIC_YEAR' ? yearId : undefined,
    from: range[0].format('YYYY-MM-DD'),
    to: range[1].format('YYYY-MM-DD'),
    thresholdPct,
  };

  const enabled =
    (scope === 'CLASS_SECTION' && !!classSectionId) || (scope === 'ACADEMIC_YEAR' && !!yearId);

  const report = useQuery({
    queryKey: ['reports', 'chronic-absentees', params],
    queryFn: () => reportsApi.chronicAbsentees(params),
    enabled,
  });

  const data = report.data;

  const columns: TableProps<ChronicAbsenteeRow>['columns'] = [
    { title: 'Reg #', dataIndex: 'registrationNumber', width: 110 },
    {
      title: 'Student',
      dataIndex: 'studentName',
      render: (v: string) => <Typography.Text strong>{v}</Typography.Text>,
    },
    {
      title: 'Class',
      dataIndex: 'classSectionName',
      width: 160,
      render: (v: string | null) => v ? <Tag>{v}</Tag> : <Typography.Text type="secondary">—</Typography.Text>,
    },
    { title: 'Absent', dataIndex: 'absent', width: 90, align: 'right' as const },
    { title: 'Half-day', dataIndex: 'halfDay', width: 90, align: 'right' as const },
    { title: 'Working days', dataIndex: 'workingDays', width: 110, align: 'right' as const },
    {
      title: 'Absence %',
      dataIndex: 'absencePct',
      width: 110,
      align: 'right' as const,
      render: (v: number) => (
        <Tag color={v >= 50 ? 'red' : v >= 30 ? 'orange' : 'gold'}>{v.toFixed(1)}%</Tag>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <PageHeader
        eyebrow="Reports"
        title="Chronic absentees"
        description="Students whose absence rate exceeds a configurable threshold over the chosen window. Half-days count as 0.5 absent; approved leave is excluded."
        actions={
          <ExportButton
            items={[{
              kind: 'xlsx',
              url: '/reports/exports/chronic-absentees.xlsx',
              params,
              fileName: `chronic-absentees-${params.from}-to-${params.to}.xlsx`,
              disabled: !enabled,
            }]}
          />
        }
      />

      <Card>
        <Row gutter={[12, 12]} align="bottom">
          <Col xs={24} md={5}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Scope
            </Typography.Text>
            <Select<Scope>
              style={{ width: '100%' }}
              value={scope}
              onChange={setScope}
              options={[
                { value: 'ACADEMIC_YEAR', label: 'Whole academic year' },
                { value: 'CLASS_SECTION', label: 'One class section' },
              ]}
            />
          </Col>
          <Col xs={24} md={5}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Academic year
            </Typography.Text>
            <Select
              style={{ width: '100%' }}
              loading={yearsQuery.isLoading}
              value={yearId}
              onChange={(v) => {
                setYearId(v);
                setClassSectionId(undefined);
              }}
              options={(yearsQuery.data ?? []).map((y) => ({
                value: y.id,
                label: y.name + (y.current ? ' (current)' : ''),
              }))}
            />
          </Col>
          {scope === 'CLASS_SECTION' && (
            <Col xs={24} md={5}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Class section
              </Typography.Text>
              <Select
                style={{ width: '100%' }}
                loading={sectionsQuery.isLoading}
                value={classSectionId}
                onChange={setClassSectionId}
                placeholder="Choose section"
                options={(sectionsQuery.data ?? []).map((cs) => ({
                  value: cs.id,
                  label: `${cs.className} · ${cs.sectionName}`,
                }))}
                showSearch
                optionFilterProp="label"
              />
            </Col>
          )}
          <Col xs={24} md={6}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Window
            </Typography.Text>
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              value={range}
              onChange={(v) => v && v[0] && v[1] && setRange([v[0], v[1]])}
              allowClear={false}
            />
          </Col>
          <Col xs={24} md={3}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Threshold %
            </Typography.Text>
            <InputNumber
              style={{ width: '100%' }}
              value={thresholdPct}
              onChange={(v) => setThresholdPct(typeof v === 'number' ? v : 20)}
              min={0}
              max={100}
            />
          </Col>
        </Row>
      </Card>

      {data && (
        <Card>
          <Row gutter={[16, 12]}>
            <Col xs={12} md={6}>
              <Statistic title="Working days" value={data.workingDays} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Threshold" value={`${data.thresholdPct.toFixed(1)}%`} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Flagged students"
                value={data.rows.length}
                styles={{ content: { color: data.rows.length > 0 ? '#cf1322' : '#389e0d' } }}
              />
            </Col>
          </Row>
        </Card>
      )}

      <Card>
        {!enabled ? (
          <Empty description="Choose academic year (and class section if scoped) to load the report." />
        ) : data && data.rows.length === 0 ? (
          <Empty description={`No students above the ${thresholdPct}% threshold in the chosen window.`} />
        ) : (
          <Table<ChronicAbsenteeRow>
            rowKey="studentId"
            dataSource={data?.rows ?? []}
            loading={report.isFetching}
            columns={columns}
            pagination={{ pageSize: 50, showSizeChanger: true }}
            size="small"
            scroll={{ x: 900 }}
          />
        )}
      </Card>
    </div>
  );
}
